import { FC, FormEvent } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, Link } from "react-router-dom";
import styles from "./Login.module.scss";
import { PulseLogo } from "../../components/PulseLogo";
import bgImage from "../../assets/images/meridian-hq.webp";

const Login: FC = () => {
  const navigate = useNavigate();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    navigate("/");
  };

  return (
    <>
      <Helmet>
        <title>Sign in to Pulse | Meridian Tower</title>
        <meta
          name="description"
          content="Sign in to Pulse building information portal."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <main className={styles.loginPage}>
        <div className={styles.bgImageWrapper}>
          <img src={bgImage} alt="" className={styles.bgImage} />
          <div className={styles.mobileOverlay} />
          <div className={styles.desktopLeftOverlay}>
            <a href="/" className={styles.brandHeader} aria-label="Pulse Home">
              <PulseLogo variant="full" theme="dark" />
            </a>

            <div className={styles.heroContent}>
              <span className={styles.eyebrow}>MERIDIAN TOWER · LONDON</span>
              <h1 className={styles.heading}>
                Building information, connected.
              </h1>
              <p className={styles.text}>
                Secure access to drawings, asset records, documents and design
                information for your site.
              </p>
            </div>

            <footer className={styles.footerMeta}>
              Pulse Data Management · Managed access
            </footer>
          </div>
        </div>

        <section className={styles.signInPanel} aria-label="Sign in">
          <div className={styles.mobileBrand}>
            <a
              href="/"
              aria-label="Pulse Home"
              style={{
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <PulseLogo variant="full" theme="light" />
            </a>
          </div>

          <div className={styles.panelHeader}>
            <span className={styles.eyebrow}>MERIDIAN TOWER · LONDON</span>
            <h2 className={styles.title}>Sign in to Pulse</h2>
            <p className={styles.subtitle}>
              Use the account provided by your Pulse administrator.
            </p>
            <p className={styles.subtextDemo}>
              Demo mode: FAKE LOGIN — enter any username or password to continue
              (any credentials will work).
            </p>
          </div>

          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.fieldGroup}>
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                name="username"
                placeholder="Enter your username"
                autoComplete="username"
                required
              />
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                placeholder="Enter your password"
                autoComplete="current-password"
                required
              />
            </div>

            <button type="submit" className={styles.submitButton}>
              Sign in
            </button>
          </form>

          <div className={styles.divider} aria-hidden="true" />

          <div className={styles.accessPanel}>
            <h3 className={styles.accessTitle}>Need access?</h3>
            <p className={styles.accessText}>
              Pulse accounts are managed by our team.{" "}
              <Link to="/more-info">Request access</Link> and we will arrange a
              login for you.
            </p>
          </div>

          <div className={styles.supportLinkContainer}>
            Having trouble signing in?{" "}
            <Link to="/more-info">Contact Pulse support</Link>
          </div>

          <div className={styles.demoNote} role="note">
            <strong>Demo note:</strong> FAKE LOGIN — ANY CREDENTIALS WILL WORK.
            This is a presentation-only login screen and does not authenticate
            or store credentials.
          </div>
        </section>
      </main>
    </>
  );
};

export default Login;
